import { lazySubscribe, NotificationSubscription, NotificationCallback } from 'owa-notification';
import reminderNotificationAction from './actions/reminderNotificationAction';
import newMailNotificationAction from './actions/newMailNotificationAction';
import socialActivityNotificationAction from './actions/socialActivityNotificationAction';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import type NotificationType from 'owa-service/lib/contract/NotificationType';
import SubscriptionMailboxType from 'owa-service/lib/contract/SubscriptionMailboxType';
import { getOwaWorkload, OwaWorkload } from 'owa-workloads';
import { isFeatureEnabled } from 'owa-feature-flags';
import reactionNotificationAction from './actions/reactionNotificationAction';
import activityFeedNotificationAction from './actions/activityFeedNotificationAction';
import type NotificationPayloadBase from 'owa-service/lib/contract/NotificationPayloadBase';
import { logUsage } from 'owa-analytics';

const PAYLOADS_RECEIVED_LOGGING_GAP: number = 20;

let isSubscriptionInitialized = false;
let payloadsReceived = 0;

export async function subscribeToNotifications() {
    if (isSubscriptionInitialized) {
        return;
    }
    isSubscriptionInitialized = true;
    await lazySubscribe.importAndExecute(
        createSubscription('ReminderNotification', 'ReminderNotifications'),
        notification =>
            logAndActOnNotificationReceived(n => reminderNotificationAction(n, null), notification)
    );

    if (getOwaWorkload() == OwaWorkload.Mail) {
        await lazySubscribe.importAndExecute(
            createSubscription('NewMailNotification'),
            notification => logAndActOnNotificationReceived(newMailNotificationAction, notification)
        );
    }

    if (!isConsumer()) {
        await subscribeToSocialActivityNotification(
            false /* isOpx */,
            notification =>
                logAndActOnNotificationReceived(reactionNotificationAction, notification),
            notification =>
                logAndActOnNotificationReceived(socialActivityNotificationAction, notification),
            notification =>
                logAndActOnNotificationReceived(activityFeedNotificationAction, notification)
        );
    }
}

function logAndActOnNotificationReceived(
    notificationCallback: NotificationCallback,
    notification: NotificationPayloadBase
) {
    payloadsReceived++;

    if (isFeatureEnabled('auth-signalrChannelLogging')) {
        logUsage('SignalRNotification', {
            NotificationType: notification.id,
            EventType: notification.EventType,
        });
    } else if (payloadsReceived % PAYLOADS_RECEIVED_LOGGING_GAP == 0) {
        logUsage('SignalRNotification', {
            PayloadsReceivedCount: payloadsReceived,
        });
    }

    notificationCallback(notification);
}

export async function subscribeToSocialActivityNotification(
    isOpx: boolean,
    reactionCallback: NotificationCallback,
    socialActivityCallBack?: NotificationCallback,
    activityFeedNotificationCallBack?: NotificationCallback
) {
    if (isOpx || getOwaWorkload() == OwaWorkload.Mail) {
        if (isFeatureEnabled('auth-reactionNotifications') && isFeatureEnabled('rp-reactions')) {
            await lazySubscribe.importAndExecute(
                createSubscription('ReactionNotification'),
                reactionCallback
            );
        } else {
            await lazySubscribe.importAndExecute(
                createSubscription('SocialActivityNotification'),
                socialActivityCallBack ?? reactionCallback
            );
        }
    }

    if (isFeatureEnabled('auth-activityFeedPushModel') && activityFeedNotificationCallBack) {
        await lazySubscribe.importAndExecute(
            createSubscription('ActivityFeedNotification'),
            activityFeedNotificationCallBack
        );
    }
}

export function subscribeToConnectedAccountNotifications(
    connectedAccount: string,
    connectedAccountType: 'Outlook' | 'Google'
) {
    lazySubscribe.importAndExecute(
        {
            subscriptionId: `ReminderNotification-${connectedAccount}`,
            requiresExplicitSubscribe: true,
            subscriptionParameters: {
                NotificationType: 'ReminderNotification',
                MailboxId: connectedAccount,
                MailboxType:
                    connectedAccountType === 'Google'
                        ? SubscriptionMailboxType.GmailSecondaryMailbox
                        : SubscriptionMailboxType.ConsumerSecondaryMailbox,
            },
        },
        notification => reminderNotificationAction(notification, connectedAccount)
    );
}

function createSubscription(
    notificationType: NotificationType,
    id?: string
): NotificationSubscription {
    return {
        subscriptionId: id || notificationType,
        requiresExplicitSubscribe: true,
        subscriptionParameters: {
            NotificationType: notificationType,
        },
    };
}
